/**
 * Utility functions for JWT token handling
 */

import {isOrganizationRole, isProviderRole, isSuperAdministratorRole} from '../constants/userRoles';

export type TokenPayload = {
    admin?: boolean;
    impersonate_id?: number | string | null;
    userId?: number | string | null;
    role_id?: number | string | null;
    type_id?: number | string | null;
    organization_id?: number | string | null;
    org_id?: number | string | null;
    permissions?: string[];
    exp?: number;
    iat?: number;
} & Record<string, unknown>;

function normalizeNullableId(value: number | string | null | undefined): string | null {
    if (value == null) return null;
    const normalized = String(value).trim();
    return normalized ? normalized : null;
}

export function getOrganizationIdFromTokenPayload(payload?: TokenPayload | null): string | null {
    // Tokens vary across auth issuers/versions; support both keys.
    return normalizeNullableId(payload?.organization_id) ?? normalizeNullableId(payload?.org_id);
}

/**
 * Decode JWT token payload
 */
export const decodeJwtToken = (token: string): TokenPayload | null => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

/**
 * Check if user is admin based on token
 */
export const isUserAdmin = (token?: string): boolean => {
    if (!token) return false;

    const payload = decodeJwtToken(token);
    return Boolean(isSuperAdministratorRole(payload?.role_id) && !payload?.impersonate_id);
};

export function isProviderOrOrganizationUser(token?: string | null): boolean {
    if (!token) return false;
    const payload = decodeJwtToken(token);
    const roleId = payload?.role_id;
    const typeId = payload?.type_id;
    const hasTypeId = typeId != null && String(typeId).trim() !== '';
    return (isProviderRole(roleId) || isOrganizationRole(roleId) || isSuperAdministratorRole(roleId)) && hasTypeId;
}
